import { useEffect, useState } from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { FlowRuleType, UpdateFlowRule } from '@/types/hydraulic'
import useTranslation from '@/hooks/useTranslation'
import InfoTip from './InfoTip'
import { useRuleCopy } from './use-rule-copy'

type Draft = Record<string, number>

export type RuleSection = 'alarm' | 'learning' | 'sensors' | 'valves'

interface RuleRailProps {
  rule?: FlowRuleType | null
  saving?: boolean
  isViewOnly?: boolean
  onSave: (values: UpdateFlowRule) => void
  onCancel?: () => void
}

const LEARNING_FIELDS = ['stableDeviation', 'stableCount', 'stableMax'] as const

/**
 * The flow-learning settings, in the same visual language as the flow alarm
 * popup: white editable fields with visible borders, readable labels, and a
 * Cancel / Save footer. The popover shell supplies the dark identity header.
 * Save writes the whole record — the API replaces it — with the alarm's
 * values carried through from the loaded rule.
 */
const RuleRail = ({ rule, saving, isViewOnly, onSave, onCancel }: RuleRailProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Draft>({})

  useEffect(() => {
    if (!rule) return
    setDraft({
      stableDeviation: Number(rule.stableDeviation ?? 0),
      stableCount: Number(rule.stableCount ?? 0),
      stableMax: Number(rule.stableMax ?? 0),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.id, rule])

  const dirty =
    !!rule &&
    LEARNING_FIELDS.some(
      (field) => String(draft[field] ?? '') !== String(Number((rule as any)[field] ?? 0)),
    )

  const set = (field: string) => (value: number) =>
    setDraft((prev) => ({ ...prev, [field]: value }))

  const handleSave = () => {
    if (!rule) return
    onSave({
      id: rule.id,
      interfaceId: rule.interfaceId,
      pipeId: rule.pipeId,
      active: Boolean(rule.active),
      autoAction: Boolean(rule.autoAction),
      allowedIdleFlow: Number(rule.allowedIdleFlow ?? 0),
      maxLowDeviation: Number(rule.maxLowDeviation ?? 0),
      maxHighDeviation: Number(rule.maxHighDeviation ?? 0),
      startDelay: Number(rule.startDelay ?? 0),
      changeDelay: Number(rule.changeDelay ?? 0),
      errorDelay: Number(rule.errorDelay ?? 0),
      stableDeviation: Number(draft.stableDeviation ?? 0),
      stableCount: Number(draft.stableCount ?? 0),
      stableMax: Number(draft.stableMax ?? 0),
    })
  }

  const field = (
    name: (typeof LEARNING_FIELDS)[number],
    label: string,
    unit: string,
    tip: string,
  ) => (
    <Stack sx={{ gap: '5px', minWidth: 0 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: '4px' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 650, color: 'grey.800' }}>
          {label}
          {unit ? (
            <Box component="span" sx={{ fontWeight: 500, color: 'grey.600' }}>
              {' '}
              {unit}
            </Box>
          ) : null}
        </Typography>
        <InfoTip text={tip} />
      </Stack>
      <TextField
        size="small"
        value={draft[name] ?? ''}
        disabled={isViewOnly}
        onChange={(event) => set(name)(Number(event.target.value))}
        inputProps={{ inputMode: 'decimal', style: { padding: '8px 10px' } }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 600,
            backgroundColor: 'background.default',
            '& fieldset': { borderColor: 'border.main' },
            '&:hover fieldset': { borderColor: 'rgba(0, 71, 68, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
        }}
      />
    </Stack>
  )

  return (
    <Box sx={{ backgroundColor: 'background.default', width: '100%', minWidth: 0 }}>
      <Stack sx={{ p: '16px 18px 14px', gap: '12px' }}>
        <Typography sx={{ fontSize: '12px', color: 'grey.600' }}>
          {copy.tips.learning}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          {field(
            'stableDeviation',
            copy.stableDeviation,
            '(%)',
            copy.tips.stableDeviation,
          )}
          {field('stableCount', copy.stableCount, '', copy.tips.stableCount)}
          {field('stableMax', copy.stableMax, '(min)', copy.tips.stableMax)}
        </Box>
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: '8px',
          px: '18px',
          py: '13px',
          borderTop: '1px solid',
          borderColor: 'border.main',
          justifyContent: 'flex-end',
        }}
      >
        {onCancel && (
          <Button variant="secondary" size="small" disabled={saving} onClick={onCancel}>
            {t('commonActionsCancel')}
          </Button>
        )}
        {!isViewOnly && (
          <Button
            variant="primary"
            size="small"
            disabled={saving || !dirty}
            onClick={handleSave}
          >
            {t('commonActionsSave')}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default RuleRail
